import React, { useMemo } from "react";
import styled from "styled-components";
import { Trans } from "@coworker/locales";
import { useTranslation } from "@coworker/locales";
import taskState from "@coworker/enums/taskState.js";
import useFormatter from "../../../hooks/useFormatter.js";
import {
  mergeQuestionRequest,
  searchMFAQ,
  useMFAQ,
} from "../hooks/useMFAQsService.js";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification.js";
import { usePeriodTimestamps } from "../../InputPopup/UserFiltersRow/usePeriodTimestamps.js";
import tracker from "../../../helpers/tracker.js";
import { isFinishedState } from "../../../constants/taskState.js";
import { useStoreId } from "../../../core/auth/useLoggedInUser.js";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton.js";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch.js";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView.js";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem.js";
import { useMfaqFilters } from "../hooks/useMfaqFilters.js";
import { DEFUALT_PERIOD_VALUE, PERIOD_ID } from "../hooks/usePeriodFilter.js";
import {
  DEFUALT_LOCATION_VALUE,
  LOCATION_ID,
} from "../hooks/useLocationFilter.js";
import { getSelectedFilterValue } from "@coworker/apprestructured/src/shared/filters/utils/filter/filtervalue.js";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets.tsx";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader.tsx";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal.tsx";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter.tsx";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate.ts";
import styles from "@coworker/apprestructured/src/shared/styles/common-styles.module.css";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage.js";

const MessageWrapper = styled("div")`
  margin: 16px;
`;

const SearchWrapper = styled("div")`
  padding: 12px;
  border-bottom: 1px solid var(--grey200);
`;

export function MfaqMergeQuestionModal({
  questionId,
  openMergeQuestionModal,
  setOpenMergeQuestionModal,
}) {
  const store_id = useStoreId();
  const mfaqFilters = useMfaqFilters({ hideQuestionsWithTasksOnly: false });
  const period = getSelectedFilterValue(
    PERIOD_ID,
    mfaqFilters.filterValues,
    DEFUALT_PERIOD_VALUE
  ).value;
  const location = getSelectedFilterValue(
    LOCATION_ID,
    mfaqFilters.filterValues,
    DEFUALT_LOCATION_VALUE
  ).value;

  const filters = useMemo(() => {
    return [period, location];
  }, [period, location]);

  const { t } = useTranslation();
  const { toTimeAgo, formatNumberShort } = useFormatter();
  const [startDate, endDate] = usePeriodTimestamps(filters);
  const { data: firstQuestionData } = useMFAQ(questionId, startDate, endDate);
  const [foundQuestions, setFoundQuestions] = React.useState([]);
  const [selectedQuestionId, setSelectedQuestionId] = React.useState(null);
  // Remove the base question from search results
  const filteredQuestions = React.useMemo(
    () =>
      foundQuestions?.filter((question) => question.question_id !== questionId),
    [foundQuestions, questionId]
  );

  const selectedQuestionExist = React.useMemo(
    () => filteredQuestions.some((q) => q.question_id === selectedQuestionId),
    [filteredQuestions, selectedQuestionId]
  );

  const selectedQuestionOngoingTasks = React.useMemo(() => {
    const list = [];
    for (const q of filteredQuestions) {
      if (q.question_id !== selectedQuestionId) continue;
      for (const task of q?.tasks || []) {
        if (!isFinishedState(task?.state)) list.push(task);
      }
    }
    return list;
  }, [filteredQuestions, selectedQuestionId]);

  // Currently, merge not allowed if both questions have ongoing tasks in any area.
  const questionHasTask =
    !!selectedQuestionOngoingTasks.length &&
    firstQuestionData?.tasks?.some(
      (task) => ![taskState.COMPLETED, taskState.CLOSED].includes(task?.state)
    );

  const { showToast } = useToastNotification();
  const navigate = useFixaNavigate();

  const mergeQuestion = async (secondQuestionId) => {
    const newQuestionId = await mergeQuestionRequest(
      questionId,
      secondQuestionId
    );
    if (newQuestionId) {
      showToast(<Trans>mfaqMergeCompletedString</Trans>);
      navigate(`/customerquestions/${newQuestionId}`);
    } else {
      //  showToast(<Trans>mergeUnsuccessString</Trans>);
      console.error("Error: MFAQ merge question", newQuestionId);
    }
  };

  React.useEffect(() => {
    // Unselect previously selected question
    if (selectedQuestionId && !selectedQuestionExist) {
      setSelectedQuestionId(null);
    }
  }, [selectedQuestionId, selectedQuestionExist]);

  // We want to track if both questions have tasks.
  if (questionHasTask) {
    tracker.MFAQ.trackMergeNotPossible(questionId, selectedQuestionId);
  }

  return (
    <FixaModal
      visible={openMergeQuestionModal}
      handleCloseBtn={() => {
        setOpenMergeQuestionModal(false);
      }}
    >
      <FixaSheets
        labelledById="mfaq-merge-question"
        size="small"
        header={
          <FixaModalHeader
            className={styles["modal-header-border"]}
            title={t("mfaqMergeQuestionString")}
          />
        }
        footer={
          <FixaModalFooter>
            <FixaButton
              data-testid="MfaqMergeQuestionModal"
              type="primary"
              disabled={!selectedQuestionId || questionHasTask}
              text={t("mfaqMergeString")}
              onClick={async (event) => {
                event.preventDefault();
                tracker.MFAQ.trackMergeButton(questionId, selectedQuestionId);
                await mergeQuestion(selectedQuestionId);
                setOpenMergeQuestionModal(false);
              }}
            />
          </FixaModalFooter>
        }
      >
        <SearchWrapper>
          <FixaSearch
            id="search"
            onChange={async (e) => {
              const found = await searchMFAQ(
                e.target.value,
                null,
                location,
                store_id
              );
              setFoundQuestions(found);
            }}
            placeholder={t("searchQuestionString")}
            onClear={() => {
              setFoundQuestions([]);
            }}
          />
        </SearchWrapper>
        <FixaListView id={"mergeQuetions"} className="filters-panel">
          {filteredQuestions?.map((question) => {
            const date = toTimeAgo(question?.last_asked_at);
            return (
              <FixaListViewItem
                key={question.id}
                id={question.id}
                inset
                control={"radiobutton"}
                onChange={() => {
                  setSelectedQuestionId(question.question_id);
                }}
                title={question.question_text}
                quantityLabel={formatNumberShort(question.upvotes)}
                description={t("lastAskedAtString", { date })}
                size="small"
                emphasised
              />
            );
          })}
        </FixaListView>
        {questionHasTask && (
          <MessageWrapper>
            <FixaInlineMessage
              variant={"cautionary"}
              body={<Trans>mergeNotPossibleMessageString</Trans>}
            />
          </MessageWrapper>
        )}
      </FixaSheets>
    </FixaModal>
  );
}
